import { Trash2, X } from 'lucide-react'
import { useEffect, useState } from 'react'
import type { Topic } from '../api/exams'
import type { Column } from './Topics'

export interface TopicDrawerProps {
  /** What is being edited: an existing Topic, or a new one with nothing in it yet. */
  topic: Topic
  /** True where this Topic is not the Exam's yet, so the footer says Add rather than Keep. */
  adding: boolean
  /** The codes the Exam already has, which a Topic being added may not take. */
  taken: string[]
  /** Every Session of every Paper, which the Topic may be examined in. */
  columns: Column[]
  /** The keys of the Sections that examine it now. */
  examinedIn: string[]
  /** The Topic as edited, and the keys of the Sections ticked for it. */
  onSave: (topic: Topic, examinedIn: string[]) => void
  onRemove: (() => void) | null
  onClose: () => void
}

/**
 * One Topic, in a panel over the grid.
 *
 * The Questions Manager's drawer, at a Topic's scale: a panel over a dimmed grid, Escape
 * closes it, and the footer carries the save. It opens straight into the form rather than
 * reading first, because a Topic is a code, a name and lines of text -- there is nothing to
 * render and check the way there is with a Question's MathML.
 *
 * Its Subtopics are one textarea, one per line, in the specification's order: the first is
 * the most central. Their codes are the API's to derive from the order, so moving a line
 * renumbers it. The Sections that examine the Topic are ticked here too.
 *
 * What it changes goes into the screen's draft, which the screen's Save writes whole, so the
 * footer keeps the change rather than sending it.
 *
 * A Topic Code is fixed once it is the Exam's. It is what every Question filed under
 * the Topic cites, and renaming it here would silently unfile all of them -- so the code
 * is typed when the Topic is added and read-only after. Correcting a wrong code is
 * removing the Topic and adding the right one, which is the same two decisions made out
 * loud.
 */
export function TopicDrawer({
  topic,
  adding,
  taken,
  columns,
  examinedIn,
  onSave,
  onRemove,
  onClose,
}: TopicDrawerProps) {
  const [draft, setDraft] = useState<Topic>(topic)
  const [lines, setLines] = useState((topic.subtopics ?? []).join('\n'))
  const [ticked, setTicked] = useState<string[]>(examinedIn)

  // Refused here rather than by the API, which answers a blank one with the framework's own
  // wording about a parameter called Name -- a sentence that names nothing on this form.
  const incomplete = !draft.code.trim() || !draft.name.trim()

  // A code is matched without regard to case, so MM1 and mm1 are one Topic.
  const twice =
    adding && taken.some((code) => code.toLowerCase() === draft.code.trim().toLowerCase())

  // The API's column width for a Subtopic's text; longer is refused whole.
  const long = lines.split('\n').findIndex((line) => line.trim().length > MAX_SUBTOPIC)

  // Escape closes, which is the one keyboard affordance a panel over content must have.
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  return (
    <>
      {/* Click-away, and the dimming that says the grid is behind rather than gone. */}
      <div className="fixed inset-0 z-40 bg-ink-900/20" onClick={onClose} aria-hidden />
      <aside
        role="dialog"
        aria-modal="true"
        aria-label={adding ? 'Add a Topic' : `Topic ${topic.code}`}
        className="fixed top-0 right-0 z-50 flex h-full w-full max-w-3xl flex-col border-l border-ink-100 bg-card shadow-xl"
      >
        <header className="flex items-center gap-3 border-b border-ink-100 p-4">
          <h2 className="font-heading text-h3 font-bold text-heading">
            {adding ? 'Add a Topic' : `Topic ${topic.code}`}
          </h2>
          <div className="ml-auto flex items-center gap-2">
            {onRemove && (
              <button
                type="button"
                className={`${quiet} border-red-700 text-red-700 hover:bg-red-50`}
                onClick={() => {
                  // Nothing cascades: the Questions filed under this code keep citing it
                  // and turn up on Coverage as not a Topic. Worth saying out loud, because
                  // "delete" usually means the other thing.
                  if (
                    window.confirm(
                      `Remove ${topic.code} from this Exam and from every Session? Questions filed under it keep the code and will show as not a Topic.`,
                    )
                  ) {
                    onRemove()
                  }
                }}
              >
                <Trash2 aria-hidden size={16} /> Remove
              </button>
            )}
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              className="rounded p-2 hover:bg-green-050"
            >
              <X aria-hidden size={18} />
            </button>
          </div>
        </header>

        <div className="flex-1 overflow-y-auto p-4">
          <Field label="Topic Code" schemaKey="topic_code">
            <input
              className={`${input} ${adding ? '' : 'bg-page text-muted'}`}
              value={draft.code}
              readOnly={!adding}
              onChange={(event) => setDraft({ ...draft, code: event.target.value })}
            />
          </Field>

          <Field label="Topic" schemaKey="topic">
            <input
              className={input}
              value={draft.name}
              onChange={(event) => setDraft({ ...draft, name: event.target.value })}
            />
          </Field>

          <Field label="Detail" schemaKey="detail">
            <textarea
              className={`${input} h-32`}
              maxLength={8000}
              placeholder="Everything the specification writes under this Topic."
              value={draft.detail ?? ''}
              onChange={(event) => setDraft({ ...draft, detail: event.target.value })}
            />
          </Field>

          <Field label="Subtopics" schemaKey="subtopics">
            <textarea
              className={`${input} h-48`}
              placeholder="One per line, most central first."
              value={lines}
              onChange={(event) => setLines(event.target.value)}
            />
          </Field>

          <fieldset className="mt-3">
            <legend className="font-bold text-heading">Examined in</legend>
            <div className="mt-1 flex flex-wrap gap-4">
              {columns.map((column) => (
                <label key={column.key} className="inline-flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={ticked.includes(column.key)}
                    onChange={(event) =>
                      setTicked(
                        event.target.checked
                          ? [...ticked, column.key]
                          : ticked.filter((key) => key !== column.key),
                      )
                    }
                  />
                  {column.label}
                </label>
              ))}
            </div>
          </fieldset>
        </div>

        <footer className="border-t border-ink-100 p-4">
          {/* Beside the button rather than on it: a title reaches whoever happens to own a
              mouse, and a disabled control takes no focus to rest it on. */}
          {incomplete && <p className="mb-3 text-muted">A Topic needs a Topic Code and a Topic.</p>}
          {twice && <p className="mb-3 text-red-700">{draft.code.trim()} is already a Topic of this Exam.</p>}
          {long >= 0 && (
            <p className="mb-3 text-red-700">
              Subtopic {long + 1} runs past {MAX_SUBTOPIC} characters.
            </p>
          )}
          <div className="flex justify-end gap-3">
            <button type="button" className={quiet} onClick={onClose}>
              Discard
            </button>
            <button
              type="button"
              className={loud}
              disabled={incomplete || twice || long >= 0}
              onClick={() =>
                onSave(
                  {
                    ...draft,
                    code: draft.code.trim(),
                    name: draft.name.trim(),
                    subtopics: lines
                      .split('\n')
                      .map((line) => line.trim())
                      .filter((line) => line !== ''),
                  },
                  ticked,
                )
              }
            >
              {adding ? 'Add Topic' : 'Keep changes'}
            </button>
          </div>
        </footer>
      </aside>
    </>
  )
}

function Field({
  label,
  schemaKey,
  children,
}: {
  label: string
  schemaKey: string
  children: React.ReactNode
}) {
  return (
    <label className="mt-3 block first:mt-0">
      {/* The space is not decoration: without it the two run together into one word in
          the accessible name, and "Topictopic" is what a screen reader announces. */}
      <span className="font-bold text-heading">{label}</span>{' '}
      <code className="ml-2 text-xs text-muted">{schemaKey}</code>
      {children}
    </label>
  )
}

/** The API's limit on one Subtopic's text. */
const MAX_SUBTOPIC = 1000

const input = 'mt-1 w-full rounded border border-ink-100 px-3 py-2'

const quiet =
  'inline-flex items-center gap-2 rounded-md border border-green-800 px-3 py-1.5 font-bold text-green-800 hover:bg-green-050 disabled:opacity-55'

const loud =
  'inline-flex items-center gap-2 rounded-md bg-green-800 px-4 py-2 font-bold text-white hover:bg-green-900 disabled:opacity-55'
